// Onglet « Parc machines ».
//
// Les machines et leurs taux sont SAISIS, contrairement aux opérations
// et aux matières qui se remplissent seules. Une quinzaine de lignes
// que Charles connaît par cœur, et dont une erreur change tous les prix
// — c'est précisément pour ça qu'on ne les laisse pas deviner.

import { useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useCrudList } from './useCrudList.js';

const EMPTY = {
  name: '', subtitle: '', marque: '', modele: '', type: 'centre_3axes',
  nb_axes: null, axe_c: false, axe_y: false, axe_b: false,
  outils_entraines: false, contre_broche: false, palpeur: false,
  diametre_max_mm: null, longueur_max_mm: null,
  course_x_mm: null, course_y_mm: null, course_z_mm: null,
  diametre_table_mm: null, charge_max_kg: null, passage_barre_mm: null,
  nb_outils: null, capacites_notes: '', status: 'active', sort_order: 0,
};

const TYPE_LABELS = {
  tour_cn: 'Tour CN', tour_conventionnel: 'Tour conventionnel',
  tour_fraiseur: 'Tour-fraiseur', centre_3axes: 'Centre 3 axes',
  centre_4axes: 'Centre 4 axes', centre_5axes: 'Centre 5 axes',
  rectifieuse: 'Rectifieuse', scie: 'Scie', presse: 'Presse', soudure: 'Soudure',
  sablage_microbillage: 'Sablage / microbillage', tribofinition: 'Tribofinition',
  mortaiseuse: 'Mortaiseuse', frettage: 'Frettage', metrologie: 'Métrologie',
  poste_manuel: 'Poste manuel',
};

/** Les capacités qui décident d'un nombre de prises, donc d'un prix. */
const CAPACITES = [
  ['contre_broche', 'contre-broche'],
  ['outils_entraines', 'outils entraînés'],
  ['axe_c', 'axe C'],
  ['axe_y', 'axe Y'],
  ['axe_b', 'axe B'],
  ['palpeur', 'palpeur'],
];

const STATUS_TONES = { active: 'ok', maintenance: 'warn', inactive: 'neutral' };
const STATUS_LABELS = { active: 'En service', maintenance: 'Maintenance', inactive: 'Hors service' };

export function MachinesPanel({ isAdmin }) {
  const list = useCrudList({ path: '/machines', key: 'machines', label: 'Machine' });

  const columns = [
    { key: 'name', label: 'Machine' },
    { key: 'subtitle', label: 'Précision', render: (m) => m.subtitle || '—' },
    { key: 'type', label: 'Type', render: (m) => TYPE_LABELS[m.type] ?? m.type },
    {
      key: 'capacites',
      label: 'Capacités',
      render: (m) => {
        const actives = CAPACITES.filter(([champ]) => m[champ]).map(([, libelle]) => libelle);
        return actives.length ? (
          <span className="row">
            {actives.map((c) => (
              <Badge key={c} tone={c === 'contre-broche' ? 'ok' : 'neutral'}>
                {c}
              </Badge>
            ))}
          </span>
        ) : (
          '—'
        );
      },
    },
    {
      key: 'capacite_dim',
      label: 'Capacité',
      render: (m) =>
        m.diametre_max_mm
          ? `Ø${m.diametre_max_mm} × ${m.longueur_max_mm ?? '?'}`
          : m.course_x_mm
            ? `${m.course_x_mm} × ${m.course_y_mm ?? '?'} × ${m.course_z_mm ?? '?'}`
            : '—',
    },
    {
      key: 'status',
      label: 'État',
      render: (m) => <Badge tone={STATUS_TONES[m.status]}>{STATUS_LABELS[m.status]}</Badge>,
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            label: '',
            render: (m) => (
              <Button size="sm" variant="ghost" onClick={() => list.setEditing(m)}>
                Modifier
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Card>
        <CardHead title="Parc machines">
          {isAdmin ? (
            <Button size="sm" variant="primary" onClick={() => list.setEditing(EMPTY)}>
              Nouvelle machine
            </Button>
          ) : null}
        </CardHead>
        <CardBody>
          {list.loading ? (
            <SkeletonRows rows={4} />
          ) : list.error ? (
            <ErrorState error={list.error} onRetry={list.reload} />
          ) : (
            <Table
              columns={columns}
              rows={list.rows}
              caption="Parc machines"
              empty={
                <EmptyState title="Aucune machine">
                  Le taux horaire de chaque machine alimente la gamme. Tant que le parc est vide,
                  les taux se saisissent à la main sur chaque opération.
                </EmptyState>
              }
            />
          )}
        </CardBody>
      </Card>

      {list.editing ? (
        <MachineForm
          machine={list.editing}
          errors={list.errors}
          pending={list.pending}
          onClose={() => list.setEditing(null)}
          onSave={list.save}
        />
      ) : null}
    </>
  );
}

function MachineForm({ machine, errors, pending, onClose, onSave }) {
  const [form, setForm] = useState(machine);
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Modal
      open
      onClose={onClose}
      title={machine.id ? form.name : 'Nouvelle machine'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" form="form-machine" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <form
        id="form-machine"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form, machine.id);
        }}
      >
        <TextField label="Nom" required value={form.name ?? ''} error={errors.name}
          onChange={(e) => set({ name: e.target.value })} />
        <TextField label="Précision" value={form.subtitle ?? ''} error={errors.subtitle}
          hint="Ce qui distingue cette machine d'une autre du même type."
          onChange={(e) => set({ subtitle: e.target.value })} />
        <div className="grid-2">
          <Select label="Type" required value={form.type ?? 'centre_3axes'} error={errors.type}
            hint="Énumération fermée : c'est l'agent 2 qui la lit pour raisonner."
            options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            onChange={(e) => set({ type: e.target.value })} />
          <NumberField label="Nombre d'axes" value={form.nb_axes} min={1} max={9}
            error={errors.nb_axes} onChange={(v) => set({ nb_axes: v })} />
        </div>

        <div className="grid-2">
          <TextField label="Marque" value={form.marque ?? ''} error={errors.marque}
            onChange={(e) => set({ marque: e.target.value })} />
          <TextField label="Modèle" value={form.modele ?? ''} error={errors.modele}
            onChange={(e) => set({ modele: e.target.value })} />
        </div>

        <fieldset className="stack">
          <legend className="caps">Capacités</legend>
          <p className="field__hint">
            C’est là-dessus que se tranche le nombre de prises, donc le prix en petite série. Une
            contre-broche fait l’avant et l’arrière d’une pièce rotative en une seule prise.
          </p>
          <div className="grid-2">
            {CAPACITES.map(([champ, libelle]) => (
              <label key={champ} className="row">
                <input type="checkbox" aria-label={libelle} checked={Boolean(form[champ])}
                  onChange={(e) => set({ [champ]: e.target.checked })} />
                <span>{libelle}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid-2">
          <NumberField label="Ø max (tournage)" unit="mm" value={form.diametre_max_mm} min={0}
            error={errors.diametre_max_mm} onChange={(v) => set({ diametre_max_mm: v })} />
          <NumberField label="Longueur max (tournage)" unit="mm" value={form.longueur_max_mm} min={0}
            error={errors.longueur_max_mm} onChange={(v) => set({ longueur_max_mm: v })} />
          <NumberField label="Course X" unit="mm" value={form.course_x_mm} min={0}
            error={errors.course_x_mm} onChange={(v) => set({ course_x_mm: v })} />
          <NumberField label="Course Y" unit="mm" value={form.course_y_mm} min={0}
            error={errors.course_y_mm} onChange={(v) => set({ course_y_mm: v })} />
          <NumberField label="Course Z" unit="mm" value={form.course_z_mm} min={0}
            error={errors.course_z_mm} onChange={(v) => set({ course_z_mm: v })} />
          <NumberField label="Passage de barre" unit="mm" value={form.passage_barre_mm} min={0}
            error={errors.passage_barre_mm} onChange={(v) => set({ passage_barre_mm: v })} />
          <NumberField label="Ø table" unit="mm" value={form.diametre_table_mm} min={0}
            error={errors.diametre_table_mm} onChange={(v) => set({ diametre_table_mm: v })} />
          <NumberField label="Charge max" unit="kg" value={form.charge_max_kg} min={0}
            error={errors.charge_max_kg} onChange={(v) => set({ charge_max_kg: v })} />
          <NumberField label="Nombre d'outils" value={form.nb_outils} min={0}
            error={errors.nb_outils} onChange={(v) => set({ nb_outils: v })} />
          <Select label="État" value={form.status} error={errors.status}
            options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            onChange={(e) => set({ status: e.target.value })} />
          <NumberField label="Ordre d'affichage" value={form.sort_order} min={0}
            error={errors.sort_order}
            onChange={(v) => set({ sort_order: v ?? 0 })} />
        </div>
      </form>
    </Modal>
  );
}

export default MachinesPanel;
